import { Button,
  Label, 
  makeStyles, 
  Link, 
  Dropdown,
  Option,
  Input,
  useId } from "@fluentui/react-components";
import { useNavigate, useLocation } from "react-router-dom";
import { ReactComponent as AdvancedLogo } from './advancedLogo.svg';
import { app } from "@microsoft/teams-js";
import LoggingService from "../services/loggingService";
import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { MessageType } from "../enums/messageType";
import { CONSTANTS } from "../constants/constants";
import { IdentityProvider } from "../enums/identityProvider";
import SessionService from "../services/sessionService";

const useStyles = makeStyles({
    container: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '90vh'
    }
});

export default function LoginRedirect(props:any) {
  const navigate = useNavigate();
  const location = useLocation();
  const classes = useStyles();
  const auth = useAuth();
  const dropdownId = useId("dropdown-default");

  const [selectedOptions, setSelectedOptions] = useState<{ [key: string]: string }[]>([]);
  const [region, setRegion] = useState<string>(localStorage.getItem(CONSTANTS.user_region) ?? '');
  const lastLoginEmail = localStorage.getItem(CONSTANTS.lastLoginEmail) ?? '';
  const [userName, setUserName] = useState<string>(lastLoginEmail);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const options:{ [key: string]: string }[] = auth.allRegions;

  useEffect(() => {
    // Check if there's an error message passed from navigation
    if (location.state?.errorMessage) {
      setErrorMessage(location.state.errorMessage);
      // Clear the navigation state
      navigate(location.pathname, { replace: true, state: {} });
    }

    let selectedRegion = options.find((item) => item.key === region)?.value;
    setRegion(selectedRegion ?? '');
    
    app.initialize().then(() => {
        app.getContext().then(async (context) => {
          sessionStorage.setItem('hostClientType', context.app.host.clientType);
          LoggingService.log(LoggingService.isLoggingEnabled() ? `LoginRedirect - Context loaded` : '', MessageType.Info);

          //Check for IDS auth code in context (deeplink callback)
          var code = context.page.subPageId;
          if (code != null && code.length > 0){
            LoggingService.log(LoggingService.isLoggingEnabled() ? `LoginRedirect - Found code in context` : '', MessageType.Info);
            context.page.subPageId = '';
            auth.getToken(code).then((result) => {
              if (result){
                LoggingService.log(LoggingService.isLoggingEnabled() ? `LoginRedirect - Navigating to tab` : '', MessageType.Info);
                navigate("/tab");
              }
            });
          }
        });
    });
  }, []);


  const handleRegionChange = (ev:any, data:any): void => {
    setSelectedOptions(data.selectedOptions);
    setRegion(data.optionText ?? '');
    options.forEach((item) => {
      if(item.value === data.optionValue){
        auth.setUserRegion(item.key)
      }
    });
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  async function handleLogin() {
    setErrorMessage('');

    if (!region) {
      setErrorMessage(CONSTANTS.regionRequiredMessage);
      return;
    }

    if (!userName || !userName.trim()) {
      setErrorMessage(CONSTANTS.emailRequiredMessage);
      return;
    }

    if (!validateEmail(userName.trim())) {
      setErrorMessage(CONSTANTS.invalidEmailMessage);
      return;
    }

    //Auto-discovery mode - discover organization and use appropriate provider
    LoggingService.log(LoggingService.isLoggingEnabled() ? `LoginRedirect - Discovering organization for ${userName}` : '', MessageType.Info);
    
    const firmDetail = await SessionService.getFirmDetail(userName);
    
    if (firmDetail && firmDetail.UseProvider === "1") {
      //Use Platform Identity
      LoggingService.log(LoggingService.isLoggingEnabled() ? `LoginRedirect - Organization uses Platform Identity` : '', MessageType.Info);
      
      if (!firmDetail.OrgReference) {
        LoggingService.log(LoggingService.isLoggingEnabled() ? `LoginRedirect - Missing OrgReference for PI` : '', MessageType.Error);
        setErrorMessage(CONSTANTS.orgReferenceMissingMessage);
        return;
      }
      
      // Store authentication details in sessionStorage for ExternalLoginIdp to use
      sessionStorage.setItem("IdentityProvider", IdentityProvider.PI);
      sessionStorage.setItem("UserName", userName);
      sessionStorage.setItem("UserOrgReference", firmDetail.OrgReference);
      
      LoggingService.log(LoggingService.isLoggingEnabled() ? `LoginRedirect - Stored user details, navigating to ExternalLoginIdp` : '', MessageType.Info);
    } else {
      //Use IDS
      LoggingService.log(LoggingService.isLoggingEnabled() ? `LoginRedirect - Organization uses IDS` : '', MessageType.Info);
      sessionStorage.setItem("IdentityProvider", IdentityProvider.IDS);
      sessionStorage.setItem("UserName", userName);
    }

    navigate("/externalLoginIdp");
  }

  return (
    <div className={classes.container}>
          <AdvancedLogo style={{ width: '20rem', height: 'auto' }} />
          {props.isTokenExpired === 'true' ? (
              <Label style={{ padding: '3rem 0rem 1rem 0rem' }}>Your session has expired. Please login to the application again.</Label>
          ):(
              <div style={{ padding: '3rem 0rem 0.5rem 0rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Label style={{ padding: '0rem 1rem 0.5rem 1rem' }}>A valid OneAdvanced Time Capture (Carpe Diem) account is required to use this app.</Label>
                  <Label style={{ padding: '0rem 1rem 0.5rem 1rem' }}>Please visit <Link href="https://www.oneadvanced.com/products/time-capture" target="blank">OneAdvanced Time Capture</Link> for more information.</Label>
                  <Label style={{ padding: '2rem 1rem 0rem 1rem' }}>Please log into your account to start using the app.</Label>
              </div>
          )}
            <Label id={dropdownId}>Please select the region</Label>
            <Dropdown
              aria-labelledby={dropdownId}
              placeholder="Select the region"
              value={region}
              selectedOptions={selectedOptions}
              onOptionSelect={(e, data) => handleRegionChange(e,data)}
              {...props}
              style={{ marginTop: '1rem', width: '300px' }}
            >
              {options.map((option) => (
                <Option key={option.key}>
                  {option.value}
                </Option>
              ))}
            </Dropdown>

            <Input
              type="email"
              placeholder="Enter your email"
              value={userName}
              onChange={(e) => setUserName(e.target.value.trim())}
              required
              style={{ marginTop: '1rem', width: '300px' }}
            />

            {errorMessage && (
              <Label style={{ color: 'red', marginTop: '0.5rem' }}>{errorMessage}</Label>
            )}

          <Button 
            disabled={!region || !userName} 
            style={{marginTop:'1rem'}} 
            appearance="primary" 
            onClick={handleLogin}
          >
            Login
          </Button>
    </div>
  )
}